import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Download as DownloadIcon, 
  ExternalLink as ExternalLinkIcon, 
  AlertTriangle as AlertTriangleIcon,
  CheckCircle as CheckCircleIcon 
} from 'lucide-react';
import { db, auth } from '@/lib/firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  Timestamp,
  limit 
} from 'firebase/firestore';
import { useToast } from "@/components/ui/use-toast";

interface PurchasedWorkflow {
  id: string;
  workflowId: string;
  title: string;
  description: string;
  purchaseDate: Timestamp;
  lastDownloadDate: Timestamp | null;
  currentVersion: string;
  latestVersion: string;
  hasUpdates: boolean;
  compatibility: string[];
  sellerId: string;
  sellerName: string;
}

interface WorkflowPurchase {
  id: string;
  workflowId: string;
  userId: string;
  purchaseDate: Timestamp;
  lastDownloadDate: Timestamp | null;
  currentVersion: string;
  amount: number;
  downloadToken: string;
}

// DownloadLink component
function DownloadLink({ 
  purchaseId, 
  version, 
  fileName,
  variant = "default",
  label = "Download"
}: { 
  purchaseId: string; 
  version: string; 
  fileName: string;
  variant?: "default" | "outline";
  label?: string;
}) {
  const { toast } = useToast();

  const handleDownload = async () => {
    try {
      // Here you would implement the actual download logic
      // This could involve generating a signed URL, checking permissions, etc.
      toast({
        title: "Starting download",
        description: `Downloading ${fileName}...`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "There was an error starting your download. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Button variant={variant} onClick={handleDownload}>
      <DownloadIcon className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [purchasedWorkflows, setPurchasedWorkflows] = useState<PurchasedWorkflow[]>([]);
  
  useEffect(() => {
    const fetchPurchasedWorkflows = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      
      setIsLoading(true);
      
      try {
        const userId = auth.currentUser.uid;
        
        // Get user's purchases
        const purchasesQuery = query(
          collection(db, 'workflowPurchases'),
          where('userId', '==', userId),
          orderBy('purchaseDate', 'desc')
        );
        
        const purchasesSnapshot = await getDocs(purchasesQuery);
        const purchases = purchasesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as WorkflowPurchase));
        
        // Get workflow details for each purchase
        const workflowPromises = purchases.map(async (purchase) => {
          try {
            const workflowRef = collection(db, 'workflows');
            const workflowQuery = query(workflowRef, where('__name__', '==', purchase.workflowId));
            const workflowSnapshot = await getDocs(workflowQuery);
            
            if (workflowSnapshot.empty) {
              return null;
            }
            
            const workflowData = workflowSnapshot.docs[0].data();
            
            // Get latest version
            const versionsQuery = query(
              collection(db, 'workflowVersions'),
              where('workflowId', '==', purchase.workflowId),
              where('isLatest', '==', true),
              limit(1)
            );
            
            const versionsSnapshot = await getDocs(versionsQuery);
            const latestVersion = versionsSnapshot.empty 
              ? { version: '1.0.0', compatibility: [] } 
              : versionsSnapshot.docs[0].data();
            
            // Get seller info
            const sellerRef = collection(db, 'users');
            const sellerQuery = query(sellerRef, where('__name__', '==', workflowData.sellerId));
            const sellerSnapshot = await getDocs(sellerQuery);
            const sellerData = sellerSnapshot.empty
              ? { displayName: 'Unknown Seller' }
              : sellerSnapshot.docs[0].data();
            
            return {
              id: purchase.id,
              workflowId: purchase.workflowId,
              title: workflowData.title,
              description: workflowData.description,
              purchaseDate: purchase.purchaseDate,
              lastDownloadDate: purchase.lastDownloadDate || null,
              currentVersion: purchase.currentVersion || '1.0.0',
              latestVersion: latestVersion.version,
              hasUpdates: purchase.currentVersion !== latestVersion.version,
              compatibility: latestVersion.compatibility?.platforms || [],
              sellerId: workflowData.sellerId,
              sellerName: sellerData.displayName || 'Unknown Seller',
            };
          } catch (error) {
            console.error(`Error fetching workflow ${purchase.workflowId}:`, error);
            toast({
              title: "Error",
              description: `Failed to load workflow details for ${purchase.workflowId}`,
              variant: "destructive",
            });
            return null;
          }
        });
        
        const workflows = (await Promise.all(workflowPromises)).filter(Boolean) as PurchasedWorkflow[];
        setPurchasedWorkflows(workflows);
      } catch (error) {
        console.error('Error fetching purchased workflows:', error);
        toast({
          title: "Error",
          description: "Failed to load your purchased workflows. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPurchasedWorkflows();
  }, [navigate, toast]);
  
  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">My Workflows</h1>
          <p className="text-muted-foreground">Manage your purchased workflows and updates</p>
        </div>
        <Button onClick={() => navigate('/marketplace')}>
          Browse Marketplace
        </Button>
      </div>
      
      <Tabs defaultValue="purchased" className="space-y-4">
        <TabsList>
          <TabsTrigger value="purchased">Purchased Workflows</TabsTrigger>
          <TabsTrigger value="updates">Available Updates</TabsTrigger>
        </TabsList>
        
        <TabsContent value="purchased" className="space-y-4">
          {isLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">Loading your workflows...</p>
              </div>
            </div>
          ) : purchasedWorkflows.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {purchasedWorkflows.map((workflow) => (
                <Card key={workflow.id} className="flex flex-col h-full">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-xl">{workflow.title}</CardTitle>
                      {workflow.hasUpdates && (
                        <Badge variant="secondary">Update Available</Badge>
                      )}
                    </div>
                    <CardDescription>
                      by {workflow.sellerName}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <p className="text-sm text-muted-foreground flex-1 mb-4">
                      {workflow.description.length > 120
                        ? `${workflow.description.substring(0, 120)}...`
                        : workflow.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 my-2">
                      {workflow.compatibility.map((platform) => (
                        <Badge key={platform} variant="outline">
                          {platform}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="text-xs text-muted-foreground mt-2 mb-4">
                      <div>Purchased: {workflow.purchaseDate.toDate().toLocaleDateString()}</div>
                      <div>Current Version: {workflow.currentVersion}</div>
                      {workflow.lastDownloadDate && (
                        <div>Last Download: {workflow.lastDownloadDate.toDate().toLocaleDateString()}</div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-auto">
                      <DownloadLink
                        purchaseId={workflow.id}
                        version={workflow.latestVersion}
                        fileName={`${workflow.workflowId}_v${workflow.latestVersion}.zip`}
                      />
                      
                      <Button 
                        variant="outline" 
                        onClick={() => navigate(`/workflow/${workflow.workflowId}`)}
                      >
                        <ExternalLinkIcon className="h-4 w-4 mr-2" />
                        Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangleIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="text-xl font-medium mb-2">No purchased workflows</h2>
              <p className="text-muted-foreground max-w-md mb-6">
                You haven't purchased any workflows yet. Visit the marketplace to discover automation workflows.
              </p>
              <Button onClick={() => navigate('/marketplace')}>
                Browse Marketplace
              </Button>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="updates" className="space-y-4">
          {isLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">Checking for updates...</p>
              </div>
            </div>
          ) : purchasedWorkflows.filter(w => w.hasUpdates).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {purchasedWorkflows
                .filter(workflow => workflow.hasUpdates)
                .map((workflow) => (
                  <Card key={workflow.id} className="flex flex-col h-full">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-xl">{workflow.title}</CardTitle>
                        <Badge>Update Available</Badge>
                      </div>
                      <CardDescription>
                        by {workflow.sellerName}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm">Current Version: {workflow.currentVersion}</div>
                        <div className="text-sm font-medium">New Version: {workflow.latestVersion}</div>
                      </div>
                      
                      <div className="flex gap-2 mt-auto">
                        <DownloadLink
                          purchaseId={workflow.id}
                          version={workflow.latestVersion}
                          fileName={`${workflow.workflowId}_v${workflow.latestVersion}.zip`}
                          variant="default"
                          label="Download Update"
                        />
                        
                        <Button 
                          variant="outline" 
                          onClick={() => navigate(`/workflow/${workflow.workflowId}`)}
                        >
                          <ExternalLinkIcon className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-xl font-medium mb-2">All workflows up to date</h2>
              <p className="text-muted-foreground max-w-md">
                You have the latest version of all your purchased workflows.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
} 